// src/services/fieldTaskApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const fieldTaskApi = createApi({
  reducerPath: 'fieldTaskApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/tasks`,
    prepareHeaders: (headers) => {
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['FieldTasks', 'TaskStats'],
  endpoints: (builder) => ({
    // Create field task - UPDATED to use businessId as query param
    createFieldTask: builder.mutation({
      query: ({ taskData, businessId }) => ({
        url: '',
        method: 'POST',
        params: { businessId }, // businessId as query parameter
        body: taskData
      }),
      invalidatesTags: ['FieldTasks', 'TaskStats']
    }),

    // Get field tasks
    getFieldTasks: builder.query({
      query: ({ businessId, status, type, priority, assignedTeam, date, month, projectId, siteId, page, limit }) => ({
        url: '',
        params: { 
          businessId, 
          status, 
          type, 
          priority, 
          assignedTeam, 
          date,
          month,
          projectId,
          siteId,
          page: page || 1,
          limit: limit || 10
        }
      }),
      providesTags: ['FieldTasks']
    }),

    // Get tasks by date range
    getTasksByDateRange: builder.query({
      query: ({ businessId, startDate, endDate }) => ({
        url: '/date-range',
        params: { businessId, startDate, endDate }
      }),
      providesTags: ['FieldTasks']
    }),

    // Get tasks for route optimization
    getTasksForRouting: builder.query({
      query: ({ businessId, date, teamIds }) => ({
        url: '/for-routing',
        params: { 
          businessId, 
          date, 
          teamIds: teamIds?.join(',') 
        }
      }),
      providesTags: ['FieldTasks']
    }),

    // Get task statistics
    getTaskStatistics: builder.query({
      query: ({ businessId, timeframe = '30d' }) => ({
        url: '/statistics',
        params: { businessId, timeframe }
      }),
      providesTags: ['TaskStats']
    }),

    // Update field task
    updateFieldTask: builder.mutation({
      query: ({ taskId, businessId, ...updateData }) => ({
        url: `/${taskId}`,
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      invalidatesTags: ['FieldTasks', 'TaskStats']
    }),

    // Delete field task
    deleteFieldTask: builder.mutation({
      query: ({ taskId, businessId }) => ({
        url: `/${taskId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['FieldTasks', 'TaskStats']
    }),

    // Assign task to team
    assignTaskToTeam: builder.mutation({
      query: ({ taskId, businessId, teamId }) => ({
        url: `/${taskId}/assign`,
        method: 'POST',
        params: { businessId },
        body: { teamId }
      }),
      invalidatesTags: ['FieldTasks']
    }),

    // Update task status
    updateTaskStatus: builder.mutation({
      query: ({ taskId, businessId, status }) => ({
        url: `/${taskId}/status`,
        method: 'PUT',
        params: { businessId },
        body: { status }
      }),
      invalidatesTags: ['FieldTasks', 'TaskStats']
    })
  })
});

export const {
  useCreateFieldTaskMutation,
  useGetFieldTasksQuery,
  useGetTasksByDateRangeQuery,
  useGetTasksForRoutingQuery,
  useGetTaskStatisticsQuery,
  useUpdateFieldTaskMutation,
  useDeleteFieldTaskMutation,
  useAssignTaskToTeamMutation,
  useUpdateTaskStatusMutation
} = fieldTaskApi;